// ===========================================
// Combined Colors Tab
// Admin Settings - Color Palette, Calendar Colors, Role Colors
// ===========================================

import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Tooltip,
  FormControlLabel,
  Switch,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as PaletteResetIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  useColorPalette,
  useAddColor,
  useRemoveColor,
  useResetColorPalette,
  useUpdateColorPalette,
  useRoleColors,
  useUpdateRoleColors,
  useResetRoleColors,
} from '../../../hooks/useAdminSettings';
import { useSchoolSettings, useUpdateSchoolSettings } from '../../../hooks/useAdmin';
import type {
  ColorPaletteColor,
  RoleColorsSettings,
  RoleColorEntry,
} from '../../../types/adminSettings.types';
import type { SpecialEventColors } from '../calendar/calendarColors';
import { DEFAULT_SPECIAL_EVENT_COLORS } from '../calendar/calendarColors';
import SettingsWriteGate from './SettingsWriteGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';

// ===========================================
// SHARED: COLOR FIELD (single hex color)
// ===========================================

interface ColorFieldProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
}

function ColorField({ label, description, value, onChange }: ColorFieldProps) {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              backgroundColor: value,
              border: '1px solid rgba(0,0,0,0.1)',
              flexShrink: 0,
            }}
          />
          <TextField
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            sx={{ width: 80 }}
            inputProps={{ style: { height: 32, padding: 0 } }}
          />
          <TextField
            value={value}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            placeholder="#RRGGBB"
            inputProps={{
              style: { fontFamily: 'monospace' },
              pattern: '^#[0-9A-Fa-f]{6}$',
            }}
            sx={{ width: 120 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

// ===========================================
// SHARED: ROLE COLOR FIELD (bg + text pair)
// ===========================================

interface RoleColorFieldProps {
  label: string;
  description: string;
  value: RoleColorEntry;
  onChange: (value: RoleColorEntry) => void;
}

function RoleColorField({ label, description, value, onChange }: RoleColorFieldProps) {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: value.background,
              color: value.text,
              width: 40,
              height: 40,
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {label.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
            Background
          </Typography>
          <TextField
            type="color"
            value={value.background}
            onChange={(e) => onChange({ ...value, background: e.target.value })}
            size="small"
            sx={{ width: 80 }}
            inputProps={{ style: { height: 32, padding: 0 } }}
          />
          <TextField
            value={value.background}
            onChange={(e) => onChange({ ...value, background: e.target.value })}
            size="small"
            placeholder="#RRGGBB"
            inputProps={{
              style: { fontFamily: 'monospace' },
              pattern: '^#[0-9A-Fa-f]{6}$',
            }}
            sx={{ width: 120 }}
          />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
            Text
          </Typography>
          <TextField
            type="color"
            value={value.text}
            onChange={(e) => onChange({ ...value, text: e.target.value })}
            size="small"
            sx={{ width: 80 }}
            inputProps={{ style: { height: 32, padding: 0 } }}
          />
          <TextField
            value={value.text}
            onChange={(e) => onChange({ ...value, text: e.target.value })}
            size="small"
            placeholder="#RRGGBB"
            inputProps={{
              style: { fontFamily: 'monospace' },
              pattern: '^#[0-9A-Fa-f]{6}$',
            }}
            sx={{ width: 120 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

// ===========================================
// COLOR PALETTE: SWATCH
// ===========================================

interface ColorSwatchProps {
  color: ColorPaletteColor;
  onRemove?: () => void;
  canRemove?: boolean;
}

function ColorSwatch({ color, onRemove, canRemove = true }: ColorSwatchProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1,
          backgroundColor: color.value,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color.textColor,
          fontWeight: 'bold',
          fontSize: '0.75rem',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        Aa
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight="medium" noWrap>
          {color.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {color.value}
        </Typography>
      </Box>
      {canRemove && onRemove && (
        <Tooltip title="Remove color">
          <IconButton size="small" onClick={onRemove} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ===========================================
// COLOR PALETTE: ADD DIALOG
// ===========================================

interface AddColorDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (color: { value: string; label: string }) => void;
  isLoading: boolean;
}

function AddColorDialog({ open, onClose, onAdd, isLoading }: AddColorDialogProps) {
  const [colorValue, setColorValue] = useState('#4580E4');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!label.trim()) {
      setError('Label is required');
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(colorValue)) {
      setError('Invalid hex color format');
      return;
    }
    onAdd({ value: colorValue.toUpperCase(), label: label.trim() });
  };

  const handleClose = () => {
    setColorValue('#4580E4');
    setLabel('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Custom Color</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 1,
                backgroundColor: colorValue,
                border: '1px solid rgba(0,0,0,0.1)',
                flexShrink: 0,
              }}
            />
            <TextField
              type="color"
              value={colorValue}
              onChange={(e) => setColorValue(e.target.value)}
              sx={{ width: '100%' }}
              inputProps={{ style: { height: 40 } }}
            />
          </Box>
          <TextField
            label="Hex Value"
            value={colorValue}
            onChange={(e) => setColorValue(e.target.value)}
            placeholder="#4580E4"
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField
            label="Color Label"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setError('');
            }}
            placeholder="e.g., Primary Blue"
            error={!!error}
            helperText={error}
            inputProps={{ maxLength: 30 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Adding...' : 'Add Color'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// SECTION 1: COLOR PALETTE
// ===========================================

function ColorPaletteSection() {
  // Every palette change needs system_settings.write (roles 1b §5.15).
  const canWriteSettings = usePermissions().can('system_settings', 'write');
  const { data: palette, isLoading, error } = useColorPalette();
  const addMutation = useAddColor();
  const removeMutation = useRemoveColor();
  const resetMutation = useResetColorPalette();
  const updateMutation = useUpdateColorPalette();

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAddColor = (color: { value: string; label: string }) => {
    addMutation.mutate(color, {
      onSuccess: () => setDialogOpen(false),
    });
  };

  const handleRemoveColor = (colorValue: string) => {
    if (window.confirm('Remove this color from the palette?')) {
      removeMutation.mutate(colorValue);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset color palette to brand defaults? Custom colors will be removed.')) {
      resetMutation.mutate();
    }
  };

  const handleToggleDefaults = (useDefaults: boolean) => {
    updateMutation.mutate({ useDefaults });
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={200} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load color palette. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title="Color Palette"
          subheader="Customize the colors available in color pickers throughout the app"
          action={
            <SettingsWriteGate fallback={null}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PaletteResetIcon />}
                  onClick={handleReset}
                  disabled={resetMutation.isPending}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setDialogOpen(true)}
                  disabled={!!palette && palette.colors.length >= 20}
                >
                  Add Color
                </Button>
              </Stack>
            </SettingsWriteGate>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={palette?.useDefaults ?? true}
                  onChange={(e) => handleToggleDefaults(e.target.checked)}
                  disabled={updateMutation.isPending || !canWriteSettings}
                />
              }
              label="Include brand default colors in pickers"
            />

            {palette && palette.colors.length === 0 ? (
              <Alert severity="info">
                No colors in palette. Add custom colors or reset to brand defaults.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {palette?.colors.map((color) => (
                  <Grid item xs={12} sm={6} md={4} key={color.value}>
                    <ColorSwatch
                      color={color}
                      onRemove={() => handleRemoveColor(color.value)}
                      canRemove={canWriteSettings && palette.colors.length > 1}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {palette && palette.colors.length >= 20 && (
              <Alert severity="warning">
                Maximum 20 colors reached. Remove a color to add more.
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary">
        These colors will appear in color pickers for instruments, lesson types, teachers, and other customizable elements.
      </Typography>

      <AddColorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAddColor}
        isLoading={addMutation.isPending}
      />
    </>
  );
}

// ===========================================
// SECTION 2: CALENDAR COLORS
// ===========================================
// Reads/writes School.settings.calendarDisplay.specialEventColors
// Same data source as the Calendar page's Display Settings modal

type RequiredSpecialEventColors = Required<SpecialEventColors>;

function CalendarColorsSection() {
  const { data: schoolSettings, isLoading, error } = useSchoolSettings();
  const updateSchoolSettings = useUpdateSchoolSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<RequiredSpecialEventColors>({
    defaultValues: DEFAULT_SPECIAL_EVENT_COLORS,
  });

  // Load from calendarDisplay.specialEventColors
  useEffect(() => {
    if (schoolSettings?.settings?.calendarDisplay) {
      const calendarDisplay = schoolSettings.settings.calendarDisplay as { specialEventColors?: SpecialEventColors };
      if (calendarDisplay.specialEventColors) {
        reset({
          ...DEFAULT_SPECIAL_EVENT_COLORS,
          ...calendarDisplay.specialEventColors,
        });
      }
    }
  }, [schoolSettings, reset]);

  const onSubmit = async (data: RequiredSpecialEventColors) => {
    setSaving(true);
    try {
      // Only `calendarDisplay.specialEventColors`: the server merges it over the stored display
      // and never reads any other key (roles plan R20). Sending the cached record back used to
      // overwrite every other page's newer saves.
      await updateSchoolSettings.mutateAsync({
        settings: { calendarDisplay: { specialEventColors: data } },
      });
      enqueueSnackbar('Calendar event colors updated', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update calendar event colors', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await updateSchoolSettings.mutateAsync({
        settings: { calendarDisplay: { specialEventColors: DEFAULT_SPECIAL_EVENT_COLORS } },
      });
      reset(DEFAULT_SPECIAL_EVENT_COLORS);
      enqueueSnackbar('Calendar event colors reset to defaults', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to reset calendar event colors', { variant: 'error' });
    } finally {
      setResetting(false);
    }
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={200} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load calendar colors. Please try again.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardHeader
            title="Calendar Event Colors"
            subheader="Customize colors for different event types on the calendar"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="hybridPlaceholder"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Booking Placeholder"
                      description="Unbooked individual session slots in hybrid lessons"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="hybridIndividual"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Hybrid (Individual)"
                      description="Booked individual sessions in hybrid lessons"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="hybridGroup"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Hybrid (Group Week)"
                      description="Group session weeks in hybrid lessons"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="meetAndGreet"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Meet & Greet"
                      description="Trial lesson appointments"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <SettingsWriteGate resource="school_setup">
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={handleReset}
              disabled={resetting || saving}
            >
              {resetting ? 'Resetting...' : 'Reset to Defaults'}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}

// ===========================================
// SECTION 3: ROLE COLORS
// ===========================================

const DEFAULT_ROLE_COLORS: RoleColorsSettings = {
  ADMIN: { background: '#4580E4', text: '#ffffff' },
  TEACHER: { background: '#FFCE00', text: '#080808' },
  PARENT: { background: '#96DAC9', text: '#080808' },
  STUDENT: { background: '#FFAE9E', text: '#080808' },
};

function RoleColorsSection() {
  // The owner tier is named by the school's owner title, never "Admin"; its colour can also be
  // changed beside the title on Roles & Permissions (the same stored value, #629).
  const ownerTitle = useOwnerTitle();
  const { data: roleColors, isLoading, error } = useRoleColors();
  const updateMutation = useUpdateRoleColors();
  const resetMutation = useResetRoleColors();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<RoleColorsSettings>({
    defaultValues: DEFAULT_ROLE_COLORS,
  });

  useEffect(() => {
    if (roleColors) {
      reset(roleColors);
    }
  }, [roleColors, reset]);

  const onSubmit = (data: RoleColorsSettings) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={200} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load role colors. Please try again.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardHeader
            title="User Role Colors"
            subheader="The colour of each kind of login on avatars and role chips (custom staff roles have their own, on Roles & Permissions)"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="ADMIN"
                  control={control}
                  render={({ field }) => (
                    <RoleColorField
                      label={ownerTitle}
                      description={`Your ${ownerTitle} logins (also set beside the title on Roles & Permissions)`}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="TEACHER"
                  control={control}
                  render={({ field }) => (
                    <RoleColorField
                      label="Teacher"
                      description="Instructors and teaching staff"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="PARENT"
                  control={control}
                  render={({ field }) => (
                    <RoleColorField
                      label="Parent"
                      description="Parents and guardians"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="STUDENT"
                  control={control}
                  render={({ field }) => (
                    <RoleColorField
                      label="Student"
                      description="Enrolled students"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending || updateMutation.isPending}
            >
              {resetMutation.isPending ? 'Resetting...' : 'Reset to Defaults'}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}

// ===========================================
// MAIN COMBINED TAB
// ===========================================

export default function ColorsTab() {
  return (
    <Stack spacing={4}>
      <ColorPaletteSection />

      <Divider />

      <CalendarColorsSection />

      <Divider />

      <RoleColorsSection />
    </Stack>
  );
}
